import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ComponentProps, HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn("fixed inset-0 z-50 bg-black/50", className)}
      {...props}
    />
  );
}

/**
 * TM-4cfb7824 — disable «close on click-outside» by default.
 *
 * Radix's default is to close the dialog on any pointer-down outside the
 * content (the overlay). Every dialog in this app is a form, wizard or
 * confirmation — an accidental misclick on the overlay was wiping typed
 * input (the upload wizard losing its chosen file mid-chunked-upload was
 * the most painful instance, ADR-020).
 *
 * Solution: preventDefault the outside-pointer-down by default. Escape,
 * the X button, the explicit Cancel buttons and `onOpenChange(false)`
 * continue to close the dialog normally. The rare dialog that wants the
 * legacy "click-outside dismisses" UX can pass its own
 * `onPointerDownOutside` (or `onInteractOutside`) handler that does not
 * call `e.preventDefault()`.
 */
export function DialogContent({
  className,
  children,
  onPointerDownOutside,
  onInteractOutside,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg sm:rounded-lg",
          className,
        )}
        onPointerDownOutside={(event) => {
          // Block the default-close path. We still forward the event to
          // any handler the caller supplied so they can run analytics /
          // focus moves; if they want to *allow* close they should call
          // their own onOpenChange or omit preventDefault — but the base
          // behavior is "stay open".
          event.preventDefault();
          onPointerDownOutside?.(event);
        }}
        onInteractOutside={(event) => {
          // Same story for the broader interact-outside (covers
          // right-click + focus loss). Keep the dialog open by default
          // and let the caller observe the event if they need to.
          event.preventDefault();
          onInteractOutside?.(event);
        }}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        className,
      )}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}
